import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, LogOut, Plus, Trash2, Pencil, Mail, Sparkles, Copy, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Logo } from "../../components/site/Logo";
import { useAuth } from "../../lib/auth";
import { api, errorText } from "../../lib/api";
import { formatDate } from "../Blog";
import { PostEditor } from "./PostEditor";
import { CaseEditor } from "./CaseEditor";
import { clearCasesCache } from "../../lib/useCases";
import { streamAI } from "../../lib/ai";
import { StaffTab } from "./StaffTab";
import { SocialDialog } from "./SocialDialog";
import { Insights } from "./Insights";
import { Share2 } from "lucide-react";

const ReplyDraft = ({ e }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setText("");
    try { await streamAI(`/admin/ai/enquiry-reply/${e.id}`, {}, setText); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const copy = async () => { await navigator.clipboard?.writeText(text); toast.success("Reply copied"); };
  const mailto = `mailto:${e.email}?subject=${encodeURIComponent("Your enquiry with The Catalyst")}&body=${encodeURIComponent(text)}`;
  return (
    <>
      <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-semibold text-white hover:bg-navy-dark disabled:opacity-60" data-testid={`enquiry-ai-reply-${e.id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {busy ? "Writing…" : text ? "Rewrite reply" : "Suggest reply"}
      </button>
      {text && (
        <div className="mt-3 w-full basis-full rounded-xl border border-navy/15 bg-navy/[0.04] p-4" data-testid={`enquiry-ai-draft-${e.id}`}>
          <textarea value={text} onChange={(ev) => setText(ev.target.value)} rows={8} className="w-full resize-y bg-transparent text-sm leading-relaxed text-ink outline-none" data-testid={`enquiry-ai-text-${e.id}`} />
          {!busy && (
            <div className="mt-2 flex gap-2">
              <a href={mailto} className="btn-primary !px-4 !py-2 text-xs" data-testid={`enquiry-ai-send-${e.id}`}><Mail className="h-3.5 w-3.5" /> Open in email</a>
              <button onClick={copy} className="btn-ghost !px-4 !py-2 text-xs" data-testid={`enquiry-ai-copy-${e.id}`}><Copy className="h-3.5 w-3.5" /> Copy</button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const STATUS_STYLES = { new: "bg-flame/10 text-flame-dark", contacted: "bg-navy/10 text-navy", closed: "bg-ink/10 text-ink/70" };

const Enquiries = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status }).catch((e) => toast.error(errorText(e)));
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    load();
  };

  if (!items.length) return <p className="py-16 text-center text-ink/60" data-testid="enquiries-empty">No enquiries yet.</p>;
  return (
    <div className="grid gap-4" data-testid="enquiries-list">
      {items.map((e) => (
        <article key={e.id} className="rounded-2xl border border-ink/10 bg-white p-5 sm:p-6" data-testid={`enquiry-${e.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">{e.name} <span className="font-normal text-ink/60">· {e.business}</span></h3>
              <p className="mt-1 text-sm text-ink/70">
                <a href={`mailto:${e.email}`} className="hover:text-flame">{e.email}</a>{e.phone && <> · <a href={`tel:${e.phone}`} className="hover:text-flame">{e.phone}</a></>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink/55">{formatDate(e.created_at)}</span>
              <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold outline-none ${STATUS_STYLES[e.status] || ""}`} data-testid={`enquiry-status-${e.id}`}>
                <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
              </select>
            </div>
          </div>
          <p className="mt-3 inline-block rounded-full bg-muted px-3 py-1 text-xs font-semibold text-ink">{e.service}</p>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/80">{e.message}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <ReplyDraft e={e} />
            <a href={`mailto:${e.email}?subject=${encodeURIComponent("Your enquiry with The Catalyst")}`} className="btn-ghost !px-4 !py-2 text-xs" data-testid={`enquiry-reply-${e.id}`}><Mail className="h-3.5 w-3.5" /> Reply</a>
            <button onClick={() => remove(e.id)} className="btn-ghost !px-4 !py-2 text-xs" data-testid={`enquiry-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </div>
        </article>
      ))}
    </div>
  );
};

const Posts = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [social, setSocial] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/posts").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/posts/${id}`);
    toast.success("Article deleted");
    load();
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setEditing({})} className="btn-primary !py-2.5" data-testid="new-post-button"><Plus className="h-4 w-4" /> New article</button>
      </div>
      <div className="divide-y divide-ink/10 rounded-2xl border border-ink/10 bg-white" data-testid="posts-list">
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`admin-post-${p.slug}`}>
            <img src={p.cover_image} alt="" className="h-14 w-20 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{p.title}</p>
              <p className="text-xs text-ink/60">{p.category} · {formatDate(p.created_at)} · {p.published ? "Published" : "Draft"}</p>
            </div>
            <div className="flex gap-2">
              {p.published && <Link to={`/blog/${p.slug}`} target="_blank" className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="View"><ExternalLink className="h-4 w-4" /></Link>}
              {p.published && <button onClick={() => setSocial(p)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Social captions" title="Social captions" data-testid={`social-post-${p.slug}`}><Share2 className="h-4 w-4" /></button>}
              <button onClick={() => setEditing(p)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Edit" data-testid={`edit-post-${p.slug}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(p.id)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Delete" data-testid={`delete-post-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <PostEditor key={editing.id || "new"} post={editing.id ? editing : null} open onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); toast.success("Article saved"); load(); }} />
      )}
      {social && <SocialDialog post={social} onClose={() => setSocial(null)} />}
    </div>
  );
};

const CaseStudies = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    clearCasesCache();
    api.get("/admin/case-studies").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    await api.delete(`/admin/case-studies/${id}`);
    toast.success("Case study deleted");
    load();
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => setEditing({})} className="btn-primary !py-2.5" data-testid="new-case-button"><Plus className="h-4 w-4" /> New case study</button>
      </div>
      <div className="divide-y divide-ink/10 rounded-2xl border border-ink/10 bg-white" data-testid="cases-list">
        {items.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`admin-case-${c.slug}`}>
            <img src={c.image} alt="" className="h-14 w-20 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{c.client}</p>
              <p className="text-xs text-ink/60">{c.industry} · Order {c.order} · {c.published ? "Published" : "Hidden"}</p>
            </div>
            <div className="flex gap-2">
              {c.published && <Link to={`/work/${c.slug}`} target="_blank" className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="View"><ExternalLink className="h-4 w-4" /></Link>}
              <button onClick={() => setEditing(c)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Edit" data-testid={`edit-case-${c.slug}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(c.id)} className="grid h-9 w-9 place-items-center rounded-full border border-ink/15 hover:bg-muted" aria-label="Delete" data-testid={`delete-case-${c.slug}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <CaseEditor key={editing.id || "new"} item={editing.id ? editing : null} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); toast.success("Case study saved"); load(); }} />
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  return (
    <main className="min-h-screen bg-canvas">
      <title>Admin | The Catalyst</title>
      <meta name="robots" content="noindex" />
      <header className="border-b border-ink/10 bg-white">
        <div className="wrap flex h-16 items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-ink/60 sm:inline">{user?.email}</span>
            <button onClick={logout} className="btn-ghost !px-4 !py-2" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="wrap py-10">
        <h1 className="font-display text-3xl font-bold text-ink sm:text-4xl">Dashboard</h1>
        <Tabs defaultValue="enquiries" className="mt-8">
          <TabsList className="h-auto max-w-full justify-start overflow-x-auto bg-white">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts">Blog posts</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">Case studies</TabsTrigger>
            {user?.role === "admin" && <TabsTrigger value="staff" data-testid="tab-staff">Team access</TabsTrigger>}
          </TabsList>
          <TabsContent value="enquiries" className="mt-6"><div className="grid gap-6"><Insights /><Enquiries /></div></TabsContent>
          <TabsContent value="posts" className="mt-6"><Posts /></TabsContent>
          <TabsContent value="cases" className="mt-6"><CaseStudies /></TabsContent>
          {user?.role === "admin" && <TabsContent value="staff" className="mt-6"><StaffTab /></TabsContent>}
        </Tabs>
      </div>
    </main>
  );
}
